import React, { useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { NotificationProvider } from "./context/NotificationContext";
import { CallProvider } from "./context/CallContext";
import IncomingCallModal from "./components/IncomingCallModal";
import PushPrompt from "./components/PushPrompt";
import OnlineBar from "./components/OnlineBar";
import MessageNotification from "./components/MessageNotification";
import PremiumExpiryBanner from "./components/PremiumExpiryBanner";
import AppBanner from "./components/AppBanner";
import AppPopup from "./components/AppPopup";
import api from "./lib/api";
import Landing from "./pages/Landing";
import Onboarding from "./pages/Onboarding";
import Discover from "./pages/Discover";
import Matches from "./pages/Matches";
import Chat from "./pages/Chat";
import Call from "./pages/Call";
import Likes from "./pages/Likes";
import Premium from "./pages/Premium";
import Selection from "./pages/Selection";
import Profile from "./pages/Profile";
import Verification from "./pages/Verification";
import PremiumReturn from "./pages/PremiumReturn";
import MobileAuth from "./pages/MobileAuth";
import Admin from "./pages/Admin";
import { APropos, CommentCaMarche, ConseilsSecurite, Conditions, Confidentialite, Contact, PUA, Remboursement, Tarifs, DemandesLegales, SuppressionCompte, SuppressionConversations, SecuriteEnfants } from "./pages/StaticPages";
import { Blog, BlogArticle } from "./pages/Blog";

const GoogleCallback = () => {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    if (!code) { navigate("/"); return; }

    (async () => {
      try {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const redirectUri = window.location.origin + "/auth/google";
        const { data } = await api.post("/auth/google", { code, redirect_uri: redirectUri });
        setUser(data);
        const mobileScheme = sessionStorage.getItem("kora_mobile_auth");
        if (mobileScheme) {
          sessionStorage.removeItem("kora_mobile_auth");
          navigate(`/auth/mobile?scheme=${encodeURIComponent(mobileScheme)}`);
          return;
        }
        if (!data.onboarded) navigate("/onboarding");
        else if (!data.verified) navigate("/verification");
        else navigate("/decouvrir");
      } catch (e) {
        navigate("/");
      }
    })();
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-kora-sand">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-kora-terracotta border-t-transparent rounded-full animate-spin mx-auto" />
        <p className="mt-4 text-kora-muted">Connexion en cours…</p>
      </div>
    </div>
  );
};

const Protected = ({ children, requireOnboarded = true, requireVerified = true }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-kora-sand">
        <div className="w-12 h-12 border-4 border-kora-terracotta border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/" replace />;
  if (requireOnboarded && !user.onboarded) return <Navigate to="/onboarding" replace />;
  if (requireVerified && user.onboarded && !user.verified) return <Navigate to="/verification" replace />;
  return children;
};

const AdminGuard = ({ children }) => {
  const { user } = useAuth();
  if (!user?.is_admin) return <Navigate to="/profil" replace />;
  return children;
};

function AppRouter() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/auth/google" element={<GoogleCallback />} />
      <Route path="/auth/mobile" element={<MobileAuth />} />
      <Route path="/a-propos" element={<APropos />} />
      <Route path="/comment-ca-marche" element={<CommentCaMarche />} />
      <Route path="/conseils-securite" element={<ConseilsSecurite />} />
      <Route path="/conditions" element={<Conditions />} />
      <Route path="/confidentialite" element={<Confidentialite />} />
      <Route path="/privacy" element={<Confidentialite />} />
      <Route path="/supprimer-compte" element={<SuppressionCompte />} />
      <Route path="/delete-account" element={<SuppressionCompte />} />
      <Route path="/supprimer-conversations" element={<SuppressionConversations />} />
      <Route path="/securite-enfants" element={<SecuriteEnfants />} />
      <Route path="/delete-conversations" element={<SuppressionConversations />} />
      <Route path="/politique-utilisation-acceptable" element={<PUA />} />
      <Route path="/politique-remboursement" element={<Remboursement />} />
      <Route path="/tarifs" element={<Tarifs />} />
      <Route path="/demandes-legales" element={<DemandesLegales />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:slug" element={<BlogArticle />} />
      <Route path="/onboarding" element={<Protected requireOnboarded={false} requireVerified={false}><Onboarding /></Protected>} />
      <Route path="/verification" element={<Protected requireVerified={false}><Verification /></Protected>} />
      <Route path="/decouvrir" element={<Protected><Discover /></Protected>} />
      <Route path="/matchs" element={<Protected><Matches /></Protected>} />
      <Route path="/chat/:matchId" element={<Protected><Chat /></Protected>} />
      <Route path="/call/:matchId" element={<Protected><Call /></Protected>} />
      <Route path="/likes" element={<Protected><Likes /></Protected>} />
      <Route path="/premium" element={<Protected><Premium /></Protected>} />
      <Route path="/selection" element={<Protected><Selection /></Protected>} />
      <Route path="/premium/retour" element={<Protected requireVerified={false}><PremiumReturn /></Protected>} />
      <Route path="/profil" element={<Protected requireVerified={false}><Profile /></Protected>} />
      <Route path="/admin" element={<Protected><AdminGuard><Admin /></AdminGuard></Protected>} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <NotificationProvider>
            <CallProvider>
              <Toaster position="top-center" richColors />
              <AppBanner />
              <AppPopup />
              <IncomingCallModal />
              <PushPrompt />
              <OnlineBar />
              <MessageNotification />
              <PremiumExpiryBanner />
              <AppRouter />
            </CallProvider>
          </NotificationProvider>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
